export interface LiveChatMessage {
  id: number;
  from: 'sa' | 'radio';
  author: string;
  radioName: string;
  content: string;
  originalContent?: string;
  sourceLang?: string;
  targetLang?: string;
  timestamp: string;
}

export interface LiveChatConversation {
  id: number;
  radioName: string;
  frequency: string;
  planName: string;
  lastMessage: string;
  lastMessageAt: string;
  unread: number;
  online: boolean;
  streamStatus: 'online' | 'offline' | 'error';
  billingStatus: 'paid' | 'pending' | 'overdue';
  billingAmount: string;
  billingNextDate: string;
  country: string;
  registeredAt: string;
  listeners: number;
  messages: LiveChatMessage[];
  transferredFromTicket?: string;
  contractType: string;
  contractStatus: 'active' | 'expiring' | 'expired';
  contractEndDate: string;
  lastPaymentAmount: string;
  lastPaymentDate: string;
  lastPaymentMethod: string;
  ticketCount: number;
  contactEmail: string;
  contactPhone: string;
}

export const streamStatusLabels: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  online: { label: 'Stream Online', color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-400' },
  offline: { label: 'Stream Offline', color: 'text-foreground-500', bg: 'bg-background-200', dot: 'bg-foreground-400' },
  error: { label: 'Stream Caído', color: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-400' },
};

export const billingStatusLabels: Record<string, { label: string; color: string; bg: string }> = {
  paid: { label: 'Al día', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  pending: { label: 'Pendiente', color: 'text-amber-600', bg: 'bg-amber-50' },
  overdue: { label: 'Vencido', color: 'text-red-600', bg: 'bg-red-50' },
};

export const liveChatConversations: LiveChatConversation[] = [
  {
    id: 1,
    radioName: 'Radio Nueva Actitud',
    frequency: '87.90 RFW',
    planName: 'Premium',
    lastMessage: 'Gracias, ya quedó resuelto el tema del storage.',
    lastMessageAt: '2026-06-05 15:30',
    unread: 0,
    online: true,
    streamStatus: 'online',
    billingStatus: 'paid',
    billingAmount: '$49/mes',
    billingNextDate: '2026-07-01',
    country: 'Argentina',
    registeredAt: '2024-03-15',
    listeners: 2840,
    contractType: 'Anual',
    contractStatus: 'active',
    contractEndDate: '2027-03-15',
    lastPaymentAmount: '$49.00',
    lastPaymentDate: '2026-06-01',
    lastPaymentMethod: 'Tarjeta Visa',
    ticketCount: 2,
    contactEmail: 'admin@nuevaactitud.com',
    contactPhone: '+54 11 4567-8901',
    messages: [
      { id: 1, from: 'radio', author: 'Martín', radioName: 'Radio Nueva Actitud', content: 'Hola, tengo un problema con el almacenamiento. Me dice que llegué al límite pero debería tener 500 GB.', timestamp: '2026-06-05 15:20' },
      { id: 2, from: 'sa', author: 'Carlos Mendoza', radioName: 'Radio Nueva Actitud', content: 'Hola Martín, déjame revisar tu cuenta. Veo que tenés 387 GB usados de 500 GB. ¿Qué error específico te aparece?', timestamp: '2026-06-05 15:22' },
      { id: 3, from: 'radio', author: 'Martín', radioName: 'Radio Nueva Actitud', content: 'Al intentar subir una foto me dice "storage lleno", pero en el panel veo 387/500.', timestamp: '2026-06-05 15:25' },
      { id: 4, from: 'sa', author: 'Carlos Mendoza', radioName: 'Radio Nueva Actitud', content: 'Ya encontré el problema. Tenías un límite legacy de 100 GB que no se actualizó. Ya lo corregí. Probá de nuevo.', timestamp: '2026-06-05 15:28' },
      { id: 5, from: 'radio', author: 'Martín', radioName: 'Radio Nueva Actitud', content: 'Gracias, ya quedó resuelto el tema del storage.', timestamp: '2026-06-05 15:30' },
    ],
  },
  {
    id: 2,
    radioName: 'TechBeats',
    frequency: '88.50 RFW',
    planName: 'Pro',
    lastMessage: '¿Cuándo estaría disponible la API para Pro?',
    lastMessageAt: '2026-06-05 14:10',
    unread: 1,
    online: false,
    streamStatus: 'online',
    billingStatus: 'pending',
    billingAmount: '$29/mes',
    billingNextDate: '2026-06-15',
    country: 'México',
    registeredAt: '2025-01-20',
    listeners: 1520,
    contractType: 'Mensual',
    contractStatus: 'expiring',
    contractEndDate: '2026-06-20',
    lastPaymentAmount: '$29.00',
    lastPaymentDate: '2026-05-15',
    lastPaymentMethod: 'PayPal',
    ticketCount: 5,
    contactEmail: 'lucia@techbeats.mx',
    contactPhone: '+52 55 1234-5678',
    messages: [
      { id: 6, from: 'radio', author: 'Lucía', radioName: 'TechBeats', content: 'Hola, quería consultar sobre la API. ¿Los planes Pro tienen acceso?', timestamp: '2026-06-05 14:05' },
      { id: 7, from: 'sa', author: 'Carlos Mendoza', radioName: 'TechBeats', content: 'Hola Lucía, actualmente la API está disponible a partir del plan Business. Sin embargo estamos evaluando abrir endpoints limitados para Pro.', timestamp: '2026-06-05 14:08' },
      { id: 8, from: 'radio', author: 'Lucía', radioName: 'TechBeats', content: '¿Cuándo estaría disponible la API para Pro?', timestamp: '2026-06-05 14:10' },
    ],
  },
  {
    id: 3,
    radioName: 'Radio Urbana 101',
    frequency: '103.50 RFW',
    planName: 'Premium',
    lastMessage: 'Perfecto, ¡muchas gracias por la ayuda!',
    lastMessageAt: '2026-06-04 11:20',
    unread: 0,
    online: true,
    streamStatus: 'online',
    billingStatus: 'paid',
    billingAmount: '$49/mes',
    billingNextDate: '2026-07-05',
    country: 'Colombia',
    registeredAt: '2024-08-10',
    listeners: 4210,
    contractType: 'Anual',
    contractStatus: 'active',
    contractEndDate: '2027-08-10',
    lastPaymentAmount: '$490.00',
    lastPaymentDate: '2026-06-05',
    lastPaymentMethod: 'Tarjeta Mastercard',
    ticketCount: 0,
    contactEmail: 'diego@urbana101.co',
    contactPhone: '+57 1 234-5678',
    messages: [
      { id: 9, from: 'radio', author: 'Diego', radioName: 'Radio Urbana 101', content: '¿Cómo configuro la moderación avanzada para filtrar links?', timestamp: '2026-06-04 11:10' },
      { id: 10, from: 'sa', author: 'Ana López', radioName: 'Radio Urbana 101', content: 'En tu panel, andá a Cabina > Moderación > Filtros y activá "Filtro de Links". Podés agregar dominios permitidos si querés.', timestamp: '2026-06-04 11:15' },
      { id: 11, from: 'radio', author: 'Diego', radioName: 'Radio Urbana 101', content: 'Perfecto, ¡muchas gracias por la ayuda!', timestamp: '2026-06-04 11:20' },
    ],
  },
  {
    id: 4,
    radioName: 'Radio Deportiva Total',
    frequency: '101.70 RFW',
    planName: 'Business',
    lastMessage: 'Listo, ya creé el evento. ¿Necesitás algo más?',
    lastMessageAt: '2026-06-05 16:00',
    unread: 0,
    online: true,
    streamStatus: 'online',
    billingStatus: 'paid',
    billingAmount: '$79/mes',
    billingNextDate: '2026-06-28',
    country: 'Argentina',
    registeredAt: '2024-06-05',
    listeners: 6800,
    contractType: 'Anual',
    contractStatus: 'active',
    contractEndDate: '2027-06-05',
    lastPaymentAmount: '$790.00',
    lastPaymentDate: '2026-06-05',
    lastPaymentMethod: 'Transferencia',
    ticketCount: 1,
    contactEmail: 'fer@deportivatotal.com',
    contactPhone: '+54 11 3333-4444',
    messages: [
      { id: 12, from: 'radio', author: 'Fernando', radioName: 'Radio Deportiva Total', content: 'Necesito ayuda para crear un evento especial para el partido del domingo.', timestamp: '2026-06-05 15:50' },
      { id: 13, from: 'sa', author: 'Carlos Mendoza', radioName: 'Radio Deportiva Total', content: 'Claro, vamos a hacerlo. Andá a Panel > Eventos > Nuevo Evento. Poné como título "Final del Torneo" y la fecha del domingo.', timestamp: '2026-06-05 15:55' },
      { id: 14, from: 'radio', author: 'Fernando', radioName: 'Radio Deportiva Total', content: 'Listo, ya creé el evento. ¿Necesitás algo más?', timestamp: '2026-06-05 16:00' },
    ],
  },
  {
    id: 5,
    radioName: 'Onda Retro FM',
    frequency: '95.30 RFW',
    planName: 'Starter',
    lastMessage: 'El stream se me corta cada 10 minutos.',
    lastMessageAt: '2026-06-05 16:45',
    unread: 2,
    online: true,
    streamStatus: 'error',
    billingStatus: 'overdue',
    billingAmount: '$9/mes',
    billingNextDate: '2026-05-20',
    country: 'Chile',
    registeredAt: '2025-09-12',
    listeners: 320,
    contractType: 'Mensual',
    contractStatus: 'expired',
    contractEndDate: '2026-05-20',
    lastPaymentAmount: '$9.00',
    lastPaymentDate: '2026-04-20',
    lastPaymentMethod: 'Tarjeta Débito',
    ticketCount: 3,
    contactEmail: 'patricia@ondaretro.cl',
    contactPhone: '+56 9 8765-4321',
    messages: [
      { id: 15, from: 'radio', author: 'Patricia', radioName: 'Onda Retro FM', content: 'Hola, necesito ayuda urgente. El stream se me corta cada 10 minutos.', timestamp: '2026-06-05 16:40' },
      { id: 16, from: 'sa', author: 'Carlos Mendoza', radioName: 'Onda Retro FM', content: 'Hola Patricia, déjame revisar tu stream. Veo que tenés una latencia alta en el servidor de origen.', timestamp: '2026-06-05 16:42' },
      { id: 17, from: 'radio', author: 'Patricia', radioName: 'Onda Retro FM', content: 'El stream se me corta cada 10 minutos.', timestamp: '2026-06-05 16:45' },
    ],
  },
  {
    id: 6,
    radioName: 'Jazz & Soul Radio',
    frequency: '99.10 RFW',
    planName: 'Pro',
    lastMessage: 'Quería saber si puedo hacer upgrade a Premium.',
    lastMessageAt: '2026-06-05 17:10',
    unread: 1,
    online: false,
    streamStatus: 'offline',
    billingStatus: 'paid',
    billingAmount: '$29/mes',
    billingNextDate: '2026-07-10',
    country: 'España',
    registeredAt: '2024-11-30',
    listeners: 890,
    contractType: 'Semestral',
    contractStatus: 'active',
    contractEndDate: '2026-12-30',
    lastPaymentAmount: '$145.00',
    lastPaymentDate: '2026-06-10',
    lastPaymentMethod: 'Stripe',
    ticketCount: 0,
    contactEmail: 'sofia@jazzsoul.es',
    contactPhone: '+34 91 234-5678',
    messages: [
      { id: 18, from: 'radio', author: 'Sofía', radioName: 'Jazz & Soul Radio', content: 'Buenas tardes, quería saber si puedo hacer upgrade a Premium manteniendo mi precio actual.', timestamp: '2026-06-05 17:05' },
      { id: 19, from: 'sa', author: 'Diana Ríos', radioName: 'Jazz & Soul Radio', content: 'Hola Sofía, el upgrade a Premium implica el cambio de precio. Pero justo tenemos una promo de 25% OFF si contratás la licencia anual.', timestamp: '2026-06-05 17:08' },
      { id: 20, from: 'radio', author: 'Sofía', radioName: 'Jazz & Soul Radio', content: 'Quería saber si puedo hacer upgrade a Premium.', timestamp: '2026-06-05 17:10' },
    ],
  },
];